import Button from "components/button";
import ButtonIcon from "components/button-icon";
import DateRangerPicker from "components/date-range-picker";
import { IconFilterActive } from "components/icons/icn-filter-active.icon";
import { IconFilterInactive } from "components/icons/icn-filter-inactive.icon";
import Sidebar from "components/sidebar";
import { SelectBox } from "components/uikit-adapter/index";
import { useToastContext } from "hooks/useToastContext";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { getDailyDeliveryStatusesAsync } from "services/daily-delivery.service";
import { searchBusinessUnit, searchFamily } from "services/filters.service";
import type { IOption } from "types/option";
import { ISO_DATE_FORMAT } from "utils/format-date";
import {
  Container,
  FilterActions,
  FilterButtonWrapper,
  FilterContent,
  FilterSection,
  FiltersWrapper,
  SidebarWrapper,
} from "./styled";

interface FilterProps {
  filter: {
    _minDeliveryDate?: string;
    _maxDeliveryDate?: string;
    FamilyIds?: string;
    BreweryIds?: number[];
    Status?: number[];
  };
  onChangeFilter: (prop: string, value: string | number[] | undefined) => void;
  searchDailyDeliveries: () => void;
}

interface LocalFilterState {
  BreweryIds: number[];
  Status: number[];
}

export const DailyDeliveryFilters = ({
  filter,
  onChangeFilter,
  searchDailyDeliveries,
}: Readonly<FilterProps>) => {
  const { openToast } = useToastContext();
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(true);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [shouldSearchAfterFilterUpdate, setShouldSearchAfterFilterUpdate] =
    useState<boolean>(false);

  const [familyOptions, setFamilyOptions] = useState<IOption[]>([]);
  const [breweryOptions, setBreweryOptions] = useState<IOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<IOption[]>([]);

  const [selectedFamily, setSelectedFamily] = useState<IOption | undefined>(undefined);
  const [selectedBreweries, setSelectedBreweries] = useState<IOption[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<IOption[]>([]);

  const [localFilters, setLocalFilters] = useState<LocalFilterState>({
    BreweryIds: filter.BreweryIds || [],
    Status: filter.Status || [],
  });

  const loadFilterOptions = useCallback(async () => {
    try {
      setIsLoadingOptions(true);

      const [familiesRaw, breweriesRaw, statuses] = await Promise.all([
        searchFamily(false, ""),
        searchBusinessUnit(false, ""),
        getDailyDeliveryStatusesAsync(),
      ]);

      const families: IOption[] = familiesRaw.map((family: { Id: number; Name: string }) => ({
        Id: family.Id,
        Description: family.Name,
      }));

      setFamilyOptions(families);
      setBreweryOptions(breweriesRaw);
      setStatusOptions(statuses);
    } catch (error) {
      openToast(intl.get("dailyDelivery.errorLoadingFilterOptions"), "negative");
    } finally {
      setIsLoadingOptions(false);
    }
  }, [openToast]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    const selected = familyOptions.find((x) => x.Id.toString() === filter.FamilyIds);
    setSelectedFamily(selected);
  }, [familyOptions, filter.FamilyIds]);

  useEffect(() => {
    const selected = breweryOptions.filter((x) => localFilters.BreweryIds.includes(x.Id));
    setSelectedBreweries(selected);
  }, [breweryOptions, localFilters.BreweryIds]);

  useEffect(() => {
    const selected = statusOptions.filter((x) => localFilters.Status.includes(x.Id));
    setSelectedStatuses(selected);
  }, [statusOptions, localFilters.Status]);

  useEffect(() => {
    setLocalFilters({
      BreweryIds: filter.BreweryIds || [],
      Status: filter.Status || [],
    });
  }, [filter.BreweryIds, filter.Status]);

  useEffect(() => {
    if (shouldSearchAfterFilterUpdate) {
      searchDailyDeliveries();
      setShouldSearchAfterFilterUpdate(false);
    }
  }, [shouldSearchAfterFilterUpdate, searchDailyDeliveries]);

  const updateLocalFilter = useCallback(
    <T extends keyof LocalFilterState>(field: T, value: LocalFilterState[T]) => {
      setLocalFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleApplyFilters = useCallback(() => {
    onChangeFilter(
      "BreweryIds",
      localFilters.BreweryIds.length > 0 ? localFilters.BreweryIds : undefined
    );
    onChangeFilter("Status", localFilters.Status.length > 0 ? localFilters.Status : undefined);
    setIsFilterOpen(false);
    setShouldSearchAfterFilterUpdate(true);
  }, [localFilters, onChangeFilter]);

  const handleClearFilters = useCallback(() => {
    setLocalFilters({
      BreweryIds: [],
      Status: [],
    });
    onChangeFilter("BreweryIds", undefined);
    onChangeFilter("Status", undefined);
    setIsFilterOpen(false);
    setShouldSearchAfterFilterUpdate(true);
  }, [onChangeFilter]);

  const hasActiveFilters =
    (filter.BreweryIds && filter.BreweryIds.length > 0) ||
    (filter.Status && filter.Status.length > 0);

  return (
    <Container>
      <FiltersWrapper>
        <DateRangerPicker
          title={intl.get("commons.deliveryDates")}
          dataDe={filter._minDeliveryDate}
          dataAte={filter._maxDeliveryDate}
          dataMin={moment().format(ISO_DATE_FORMAT)}
          maxDate={
            filter._minDeliveryDate
              ? moment(filter._minDeliveryDate).add(3, "M").format(ISO_DATE_FORMAT)
              : undefined
          }
          handleChange={(prop: string, value: string) =>
            onChangeFilter(prop === "dataDe" ? "_minDeliveryDate" : "_maxDeliveryDate", value)
          }
        />
        <div style={{ width: "200px" }}>
          <SelectBox
            name="family"
            label={intl.get("commons.family")}
            placeholder={
              isLoadingOptions ? intl.get("commons.loading") : intl.get("commons.family")
            }
            valueKey="Id"
            labelKey="Description"
            value={selectedFamily ?? []}
            options={familyOptions}
            onChange={(value: IOption) => onChangeFilter("FamilyIds", value?.Id.toString())}
            clearable
            isDisabled={isLoadingOptions}
          />
        </div>
        <Button
          buttonType="primary"
          disabled={!filter._minDeliveryDate || !filter._maxDeliveryDate || !filter.FamilyIds}
          value={intl.get("filters.search")}
          onClick={searchDailyDeliveries}
          className="btn"
          style={{ width: "90px" }}
        />
      </FiltersWrapper>

      <FilterButtonWrapper>
        <ButtonIcon
          buttonType="secondary"
          buttonText={intl.get("filters.filter")}
          buttonIcon={
            hasActiveFilters ? <IconFilterActive fillColor="#55c9d8" /> : <IconFilterInactive />
          }
          onClick={() => setIsFilterOpen(true)}
          style={{ width: "100px" }}
          data-testid="filter-button"
        />
      </FilterButtonWrapper>

      <SidebarWrapper>
        <Sidebar
          visible={isFilterOpen}
          triggerClose={() => setIsFilterOpen(false)}
          className="daily-delivery-sidebar"
        >
          <FilterContent>
            <FilterSection>
              <SelectBox
                name="breweries"
                label={intl.get("commons.destiny")}
                placeholder={
                  isLoadingOptions ? intl.get("commons.loading") : intl.get("commons.destiny")
                }
                valueKey="Id"
                labelKey="Description"
                value={selectedBreweries}
                options={breweryOptions}
                onChange={(value: IOption[]) =>
                  updateLocalFilter("BreweryIds", value ? value.map((v) => v.Id) : [])
                }
                clearable
                isMulti
                isDisabled={isLoadingOptions}
                data-testid="brewery-select"
              />
            </FilterSection>

            <FilterSection>
              <SelectBox
                name="status"
                label={intl.get("dailyDelivery.status")}
                placeholder={
                  isLoadingOptions ? intl.get("commons.loading") : intl.get("dailyDelivery.status")
                }
                valueKey="Id"
                labelKey="Description"
                value={selectedStatuses}
                options={statusOptions}
                onChange={(value: IOption[]) =>
                  updateLocalFilter("Status", value ? value.map((v) => v.Id) : [])
                }
                clearable
                isMulti
                isDisabled={isLoadingOptions}
                data-testid="status-select"
              />
            </FilterSection>

            <FilterActions>
              <Button
                buttonType="primary"
                onClick={handleApplyFilters}
                value={intl.get("filters.search")}
                className="btn-close-search"
              />
              <Button
                buttonType="default"
                onClick={handleClearFilters}
                value={intl.get("filters.cleanSearch")}
                className="btn-clean"
              />
            </FilterActions>
          </FilterContent>
        </Sidebar>
      </SidebarWrapper>
    </Container>
  );
};
